<template>
  <div
    @drop.prevent="imgChanged"
    @dragover.prevent=""
    @dragenter.prevent="dragEnter"
    @dragleave.prevent="dragLeave"
  >
    <slot />

    <div class="input-box-holder" v-validate="errors">
      <div class="validation-output text-left mb-5">
        <ErrorHandler class="error" :id="`${type}-${personIndex}`">
          <slot name="errorLabel"/>
        </ErrorHandler>
        <ErrorHandler class="error" :id="`AT_LEAST_ONE_OF_ATTACHMENTS_IS_NOT_VALID`">
          Delete Any Files Containing Errors Before Proceeding
        </ErrorHandler>
        <ErrorHandler class="error" :id="`ATTACHMENTS_SIZE`">
          
    The files have exceeded the maximum total size for uploaded files (20 MB)
  
        </ErrorHandler>
        <ErrorHandler class="error" :id="`EXCEEDED_MAX_LIMIT`">
          You have exceeded the maximum number of uploaded files (20).
        </ErrorHandler>
      </div>

      <!-- Attachments list -->
      <div v-if="currentAttachments.length">
        <div
          v-for="attachment in currentAttachments"
          :key="attachment.index"
        >
          <div v-if="attachment.maxSizeError && attachment.formatError && attachment.minSizeError">
            <ErrorHandler class="text-left mb-5">
              The file has exceeded the maximum size (5 MB) and its format is not supported.
            </ErrorHandler>
          </div>

          <div v-else>
            <ErrorHandler v-if="attachment.maxSizeError" class="text-left mb-5">
              Exceeded the maximum file size (5 MB).
            </ErrorHandler>

            <ErrorHandler v-if="attachment.minSizeError" class="text-left mb-5">
              The minimum file size is 1 kb.
            </ErrorHandler>

            <ErrorHandler v-if="attachment.formatError" class="text-left mb-5">
              Unsupported file format.
            </ErrorHandler>
          </div>

          <div
            class="fl-upload-container"
            :key="attachment.index"
          >
            <p class="fl-container-text weight-regular body-2 color-gray-400">
              {{ attachment.file.name }}
            </p>

            <Modal
              v-if="isModalOpened"
              title='Are you sure you want to delete the uploaded document?'
              description='This is a mandatory document necessary to complete the application process.'
              buttonTitle='Delete'
              @close="hideModal"
              @confirm="removeFile(attachementToDelete)"
            />

            <!-- Attachment size anc action buttons -->
            <Button class="p-0 b-none" @click="openModal(attachment)">
              <div class="fl-container-icon">
                <IconCross/>
              </div>
            </Button>
          </div>
        </div>
      </div>

      <div class="fl">
        <div class="fl-content">
          <img :src="require('@/assets/svg/file-load-icon.svg')" alt="File load image"/>
          <h3 class="fl-title">
            Drag & Drop a File <span>or</span>
          </h3>
        </div>

        <!-- Attachment hidden input -->
        <div class="fl-btn-container">
          <input
            multiple
            type="file"
            :id="`upload-${type}-${personIndex}`"
            style="display: none"
            :accept="accept"
            @change="imgChanged"
          />
          <Button @click="popupFileWindow" class="btn-outline-primary btn-block">
             Upload File
          </Button>
        </div>
      </div>

      <InfoBox class="mt-15" :icon="require('@/assets/svg/sound-icon.svg')">
        <p class="mt-0 mb-3">
          Max. File Size: 5 MB
        </p>
        <p class="mt-0 mb-3">
          Max. Total File Size: 20 MB
        </p>
        <p class="mt-0 mb-3">
          Supported Formats: jpg, jpeg, png, pdf, doc, docx, xls, xlsx
        </p>
      </InfoBox>
    </div>
  </div>
</template>

<script>
import { ACCEPT_FILE_FORMATS, MAX_SIZE_OF_ATTACHMENT, MIN_SIZE_OF_ATTACHMENT } from '@/config';
import Button from '@/components/Button/Button.vue';
import InfoBox from '@/components/InfoBox/InfoBox.vue';
import IconCross from '@/components/Icons/IconCross.vue';
import Modal from '@/components/Modal/Modal.vue';
import ErrorHandler from '@/components/ErrorHandler/ErrorHandler.vue';

export default {
  name: 'FileLoader',
  components: {
    ErrorHandler,
    Button,
    InfoBox,
    IconCross,
    Modal,
  },
  props: {
    type: { required: true, type: String },
    personIndex: { required: true, type: Number },
    model: { type: Object, required: true },
    errors: { type: Array, required: false },
  },
  data: () => ({
    counter: 0,
    hover: false,
    isModalOpened: false,
    attachementToDelete: null,
  }),
  computed: {
    /*
    * Computed returns array of accept formats
    * */
    accept() {
      return ACCEPT_FILE_FORMATS;
    },
    acceptFormats() {
      return this.accept
        .split(',')
        .map((format) => format.trim())
        .filter((format) => format);
    },
    /*
    * Returns attachments of all persons
    * */
    attachments() {
      return this.$store.getters.getAttachments;
    },
    /*
    * Returns attachments for current upload components
    * */
    currentAttachments() {
      return this.attachments
        .map((attachment, index) => ({ ...attachment, index }))
        .filter((attachment) => attachment.type === this.type && attachment.personIndex === this.personIndex);
    },
  },
  methods: {
    openModal(attachement) {
      this.attachementToDelete = attachement;
      this.isModalOpened = !this.isModalOpened;
    },
    hideModal() {
      this.isModalOpened = false;
    },
    popupFileWindow() {
      window.document.getElementById(`upload-${this.type}-${this.personIndex}`).click();
    },
    /*
    * Returns array of object with file and validation
    * If you add new validation don't forget add also this validation to attachmentValidations.js mixin.
    * */
    validateFiles(files) {
      return files.map((file) => {
        const { size, type, name } = file;
        const extension = /(?:\.([^.]+))?$/.exec(name)[1];
        let maxSizeError = false;
        let minSizeError = false;
        let formatError = false;

        if (this.acceptFormats.every((format) => format !== type && `.${extension}` !== format)) formatError = true;

        if (size > MAX_SIZE_OF_ATTACHMENT) maxSizeError = true;

        if (size < MIN_SIZE_OF_ATTACHMENT) minSizeError = true;

        return {
          extension,
          file,
          maxSizeError,
          minSizeError,
          formatError,
          personIndex: this.personIndex,
          type: this.type,
        };
      });
    },
    /*
    * Method triggered when files change
    * */
    imgChanged(evt) {
      this.hover = false;
      this.counter = 0;

      const newFiles = evt.target.files || evt.dataTransfer.files;
      if (!newFiles) return;

      const validatedFiles = this.validateFiles(Array.from(newFiles));

      const formData = new FormData();
      validatedFiles.forEach((file) => {
        formData.append('file', file.file, file.name);
        this.$axios.post('/uzp_api/japi/IUzpApi/addAttachment', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }).then((response) => {
          const uuid = response.data;
          const { Attachment_init } = this.$kotlin.model;
          const newAttachment = Attachment_init();
          const nextAttachmentIndex = this.nextAttachmentIndex(this.type, file.personIndex);
          newAttachment.name = `${this.type}-${file.personIndex}-${nextAttachmentIndex}`;
          newAttachment.uuid = uuid;
          newAttachment.extension = file.extension;
          newAttachment.type = this.type;
          newAttachment.personIndex = file.personIndex;
          newAttachment.attachmentIndex = nextAttachmentIndex;
          this.model.attachments.array_hd7ov6$_0.push(newAttachment);
          file.uuid = uuid;
          this.$store.commit('ADD_ATTACHMENTS', [file]);
        });
      });
    },
    /*
    * Method remove file
    * */
    removeFile(attachement) {
      const { uuid, index } = attachement;
      this.$axios.get(`/uzp_api/japi/IUzpApi/deleteAttachment?uuid=${uuid}`).then(() => {
        this.model.attachments.array_hd7ov6$_0 = this.model.attachments.array_hd7ov6$_0.filter((att) => att.uuid !== uuid);
        this.$store.commit('REMOVE_ATTACHMENT', index);
        this.isModalOpened = false;
      });
    },
    /*
    * On drag enter show hover effect
    * */
    dragEnter() {
      this.counter += 1;
      this.hover = true;
    },
    /*
    * On drag enter hide hover effect
    * */
    dragLeave() {
      this.counter -= 1;
      if (this.counter === 0) this.hover = false;
    },
    /*
    * Returns index of next attachment for selected type and personIndex
    * */
    nextAttachmentIndex(type, personIndex) {
      const attachmentsArray = this.model.attachments.array_hd7ov6$_0;
      if (!attachmentsArray) return 0;
      const attachmentGroup = this.model.attachments.array_hd7ov6$_0
        .filter((att) => att.type === type && att.personIndex === personIndex);
      if (attachmentGroup.length === 0) return 0;
      return attachmentGroup[attachmentGroup.length - 1].attachmentIndex + 1;
    },
  },
};
</script>
<style lang="scss" scoped>
@import "~union_styles/scss/variables";
@import "~union_styles/scss/helpers/mixins/flexbox";
@import "~union_styles/scss/helpers/mixins/position";

$color-primary: #5DB100;

.upload-container {
  $self: &;
  @include f-center();
  flex-direction: column;
  transition: all .4s ease;

  .file-name {
    word-break: break-all;
  }

  .icon {
    &--attachment-green, &--attachment-orange {
      min-width: 47.7px;
    }
  }

  &__upload-btn {
    border-radius: 8px;
    background-color: $color-primary;
    color: $color-light;
  }

  &__file-group {
    list-style-type: none;
    width: 100%;
    margin-bottom: 30px;
  }

  &__file {
    $file_self: &;
    @include f-vertical-center();
    background-color: $color-light;
    justify-content: space-between;
    width: 100%;
    display: flex;
    border: 1px solid $border-color-primary;

    &:last-child {
      margin-bottom: 0;
    }

    &__size {
      white-space: nowrap;
    }

    &__error {
      display: none;
    }

    &__upload-remove {
      display: none;
      border: 1px solid $border-color-primary;
      font-size: 13px;
      min-height: unset;
      border-radius: 6px;
      margin-left: 46.6px;
      margin-top: 10px;
      @media (max-width: 480px) {
        display: block;
      }
    }

    &--invalid {
      border-color: $color-warning;

      #{$file_self}__size {
        color: $color-warning;
      }

      #{$file_self}__error {
        display: block;
      }
    }
  }

  &--has-file {
    padding: 40px 30px;

    #{$self}__upload-btn {
      align-self: flex-start;
    }

    #{$self}__upload-info {
      text-align: left;
      align-self: flex-start;
    }
  }

  &--hover {
    border-style: solid;
    border-color: $color-primary;
    background-color: rgba($color-primary, 0.02);
  }

  &--invalid {
    .snack-bar.orange {
      display: flex !important;
    }

    border-color: $color-warning !important;
    border-style: solid;
  }

  @media (max-width: 768px) {
    padding: 0;
    border: none !important;

    &__upload-btn {
      align-self: flex-start;
    }
    &__upload-info {
      align-self: flex-start;
      text-align: left
    }

    &__file {
      padding: 12px 22px;
    }

    .icon {
      &--attachment-green, &--attachment-orange {
        height: 40px;
        width: 36.6px;
        min-width: 36.6px;
      }
    }
  }

  @media (max-width: 480px) {
    &__file {
      align-items: flex-start;
      flex-direction: column;

      &__size {
        padding-top: 5px;
        padding-left: 46.6px;
      }

      .icon.icon--cross-gray {
        display: none;
      }
    }
  }

}
</style>
